import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { NetworkConstant } from '../network/network-constant';
import { LocalStorageManagerService } from '../storage/local-storage-manager.service';
import { AuthStore } from '../store/auth.store';
import { LoggerService } from '../logger/logger.service';
import {
  AnalyticsEvents,
  AnalyticsLogTypes,
  AnalyticsPages,
  AnalyticsUsecases,
  PROGRAM_NAME,
} from './analytics.constants';
import type { AnalyticsUsecase } from './analytics.constants';
import { buildPlatformMetadata } from './device-context';
import type {
  LogParam,
  PlatformMetadata,
  UserLogIdentity,
  UserLogRequest,
} from './analytics.types';

const TAG = 'AnalyticsService';

/**
 * Functional logs for WI 37464 — `POST api/log/user`.
 *
 * Body is flat and entirely snake_case. `interaction_time` is stamped by the BE,
 * so it is never sent.
 *
 * The client raises only the events in {@link AnalyticsEvents}: `signup_page`
 * page_load / account_selected, `recharge_page` page_load / go_back / proceed, and
 * the `home_page` page_load that closes a journey without carrying its marker.
 * Everything else in the spec
 * (`continue`, password_entered, otp_submit/otp_resend) is raised backend-side.
 *
 * Also the single source of `platform_metadata` and the journey marker for the four
 * auth APIs that must carry them (mobile/subscribers, otp/send, otp/verify,
 * user/login) — see {@link requestFields}, used by AuthDataSourceService.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private http = inject(HttpClient);
  private localStorageManager = inject(LocalStorageManagerService);
  private authStore = inject(AuthStore);
  private logger = inject(LoggerService);
  private base = environment.envDomainUrl;

  /**
   * Device/platform snapshot plus the persisted device id. Built once: neither changes for the
   * life of the page (`jio_device_id` is never rotated and survives logout), and a reload
   * rebuilds it anyway.
   */
  private readonly platformMetadata: PlatformMetadata = {
    ...buildPlatformMetadata(),
    ...this.deviceIdField(),
  };

  /**
   * First-time-user journey marker, held here on the root singleton so it outlives
   * the component that identified it: `LoginPhoneNumberComponent` determines it, but
   * later events and auth requests elsewhere still need it.
   *
   * `null` means a returning user, and the field is then omitted entirely — its
   * presence is what distinguishes a first-time user from a second-time one.
   * In-memory only: a reload clears it, which is correct, since a reloaded session
   * is no longer mid-signup.
   */
  private usecase: AnalyticsUsecase | null = null;

  /** Called once the sign-up journey is identified (see routeAfterSubscribersLookup). */
  setUsecase(usecase: AnalyticsUsecase): void {
    this.usecase = usecase;
  }

  clearUsecase(): void {
    this.usecase = null;
  }

  /**
   * Journey + device fields to merge into an auth API request body.
   * `mobile/subscribers` is the call that *determines* the journey, so it passes
   * `includeUsecase: false` and sends `platformMetadata` only.
   */
  requestFields(includeUsecase = true): {
    usecase_identification?: string;
    platform_metadata: PlatformMetadata;
  } {
    return {
      ...(includeUsecase && this.usecase ? { usecase_identification: this.usecase } : {}),
      platform_metadata: this.platformMetadata,
    };
  }

  /**
   * Fire-and-forget click event. `log(event, page_title, param?)`.
   *
   * `param.user_lead` seeds the pre-auth lead identity; `status`, `error_text` and
   * `account_details` pass through as top-level fields where the spec defines them.
   */
  log(event: string, page_title: string, param?: LogParam): void {
    const { user_lead: leadFromParam, usecase_identification, logType, ...rest } = param ?? {};

    // Per-call override wins; otherwise the journey marker, if we're in one.
    const resolvedUsecase =
      (usecase_identification as AnalyticsUsecase | undefined) ?? this.usecase;
    // `login_page` and `home_page` are never tagged with a usecase marker. A journey
    // marker can remain live until the first dashboard mount, but it must be cleared
    // only after constructing the home-page request so later events do not inherit it.
    const usecase =
      page_title === AnalyticsPages.LOGIN_PAGE || page_title === AnalyticsPages.HOME_PAGE
        ? null
        : resolvedUsecase;

    const body: UserLogRequest = {
      program: PROGRAM_NAME,
      page_title,
      event,
      logType: logType ?? AnalyticsLogTypes.CLICK_EVENT,
      platform_metadata: this.platformMetadata,
      ...(usecase ? { usecase_identification: usecase } : {}),
      ...this.safeIdentityFields(
        typeof leadFromParam === 'string' ? leadFromParam : undefined,
        resolvedUsecase,
      ),
      ...rest,
    };

    this.http.post(`${this.base}${NetworkConstant.USER_LOG}`, body).subscribe({
      next: () => {},
      error: (err) => {
        this.logger.debug(TAG, 'log request failed (ignored)', err);
      },
    });

    // The journey closes the moment the user lands on home. The home-page request
    // itself is intentionally untagged; clearing here keeps later dashboard/profile
    // activity (the recharge CTAs) untagged as well.
    if (page_title === AnalyticsPages.HOME_PAGE && event === AnalyticsEvents.PAGE_LOAD) {
      this.clearUsecase();
    }
  }

  /**
   * `identityFields` with a hard guarantee of not throwing.
   *
   * It reads `localStorage` synchronously (`getSubscriptionId`), which can raise a SecurityError
   * when storage is blocked or partitioned. `log()` is fire-and-forget and is called straight
   * from click handlers, so an exception here would surface as a broken interaction — a logging
   * concern must never do that. Identity is dropped from the event instead.
   */
  private safeIdentityFields(
    leadFromParam?: string,
    usecase?: AnalyticsUsecase | null,
  ): UserLogIdentity {
    try {
      return this.identityFields(leadFromParam, usecase);
    } catch (err) {
      this.logger.debug(TAG, 'identity resolution failed (event sent without it)', err);
      return {};
    }
  }

  /**
   * Identity per the spec, resolved centrally rather than passed by 40-odd call sites: the spec
   * repeats `subscriber_id` and `subscription_id` on all 32 post-login blocks.
   *
   * Each is omitted when unknown rather than sent empty, so absence means "not available" rather
   * than "no value". Note `subscription_id` is unavoidably absent on the earliest post-login
   * events of a cold load: it depends on storage hydration (an async AES decrypt, see `app.ts`)
   * and on the subscriptions response having resolved.
   *
   * The spec's third identity field, `account_id`, is deliberately **not** sent — open point,
   * 2026-08-14. Its only source is the access token's `accountId` claim, which would mean
   * decoding a JWT purely to satisfy a log field, so it is parked pending a backend decision on
   * whether the client should send it at all.
   *
   * `user_lead` is the pre-auth alternative to `subscriber_id`, scoped to the
   * `new_account_page` journey — the no-plan-yet path, where there is no subscriber to identify
   * and the mobile number is the only handle on the lead. Every other journey either has a
   * `subscriber_id` already or will shortly, so sending the number there would be redundant PII
   * on the wire.
   *
   * The device is identified separately and always: the persisted `jio_device_id` UUID is sent
   * as `platform_metadata.device_id` on every event (never top-level), so a visitor with neither
   * id — e.g. a URL open that stops on the install gate — is still counted, and one browser's
   * events join across the whole user cycle (guest → signup → login → logout → next login).
   */
  private identityFields(
    leadFromParam?: string,
    usecase?: AnalyticsUsecase | null,
  ): UserLogIdentity {
    const user = this.authStore.user();
    const subscriberId =
      user?.subscriberId?.trim() || this.localStorageManager.getSubscriberId()?.trim() || '';

    // Gated on a *live* access token, i.e. on being genuinely post-login. The spec puts
    // subscription_id on all 32 post-login blocks and on none of the pre-login ones, and the gate
    // also stops a stale `jio_subscription_id` left behind by an expired session (no logout, so
    // no clearAll) from being attached to the next visitor's pre-login signup events.
    //
    // Expiry is checked, not just presence: a failed silent refresh at startup leaves the expired
    // token in TokenStoreService (app.ts logs `silent-refresh-failed` and moves on), so
    // "a token exists" alone would leave that stale-attachment hole open. `jio_token_expiry` is
    // written on both login and refresh (see TokenRefreshService), so it stays in step.
    const hasLiveSession =
      Boolean(this.localStorageManager.getAccessToken()?.trim()) &&
      !this.localStorageManager.isAccessTokenExpired();
    const subscriptionId = hasLiveSession
      ? this.localStorageManager.getSubscriptionId()?.trim() || ''
      : '';

    const subscription: UserLogIdentity = {
      ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
    };

    if (subscriberId) return { subscriber_id: subscriberId, ...subscription };

    if (usecase === AnalyticsUsecases.NEW_ACCOUNT_PAGE) {
      const mobile =
        leadFromParam?.trim() ||
        user?.mobile?.trim() ||
        this.localStorageManager.getMobileNumber()?.trim() ||
        '';
      if (mobile) return { user_lead: mobile, ...subscription };
    }

    return subscription;
  }

  /**
   * The persisted `jio_device_id` UUID as `{ device_id }`, or `{}` when storage is unavailable
   * (private mode, blocked storage) — the event still goes out, just without the id.
   */
  private deviceIdField(): { device_id?: string } {
    try {
      const deviceId = this.localStorageManager.getDeviceId()?.trim();
      return deviceId ? { device_id: deviceId } : {};
    } catch (err) {
      this.logger.debug(TAG, 'device id unavailable (event sent without it)', err);
      return {};
    }
  }
}
